import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import DashboardSelector from './DashboardSelector';
import { hasFeature, isLegacyAdmin, isSuperAdmin } from '../lib/features';
import AgentWidget from './AgentWidget';
import { isRetired } from '../config/retiredPages';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import axios from 'axios';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  HandCoins,
  CreditCard,
  Upload,
  LogOut,
  PieChart,
  FileUp,
  DollarSign,
  FileText,
  FileSignature,
  Link2,
  ClipboardCheck,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  Skull,
  Settings,
  Activity,
  Layers,
  BookOpen,
  Landmark,
  Receipt,
  User,
  Sheet,
  GitBranch,
  Scale,
  Brain,
  MessageSquare,
  Inbox,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 10/23 (Josh): "a drop down … to go back and forth between the homescreen tiles — AI Assistant,
// PFS Portal, Signport & now Funding Confirmation". Same four destinations as pages/Landing.js.
const WORKSPACES = [
  { id: 'ai', label: 'AI Assistant', hint: 'Underwriting & decision support', href: 'https://ai.powerclaw.win' },
  { id: 'portal', label: 'PFS Portal', hint: 'Deals, syndication & reporting', to: '/dashboard' },
  { id: 'signport', label: 'Signport', hint: 'Contracts & e-signatures', to: '/signport' },
  { id: 'confirmation', label: 'Funding Confirmation', hint: 'Merchant pre-funding acknowledgments', to: '/funding-confirmations' },
];
const currentWorkspace = (pathname) => (
  pathname.startsWith('/funding-confirmations') ? 'confirmation' : pathname.startsWith('/signport') ? 'signport' : 'portal'
);

const WorkspaceSwitcher = ({ pathname, navigate }) => {
  const active = WORKSPACES.find((w) => w.id === currentWorkspace(pathname)) || WORKSPACES[1];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex items-center gap-2 rounded-md px-1 py-0.5 text-left hover:bg-slate-100" data-testid="workspace-switcher" title="Switch workspace">
          <div className="h-8 w-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-bold">PFS</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 flex items-center gap-1">{active.label}<ChevronDown className="h-3.5 w-3.5 text-slate-400" /></p>
            <p className="text-xs text-slate-500 truncate">Power Funding Solutions</p>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {WORKSPACES.map((w) => (
          <DropdownMenuItem key={w.id} className={w.id === active.id ? 'bg-slate-100' : ''}
            onSelect={() => { if (w.href) window.location.href = w.href; else navigate(w.to); }} data-testid={`workspace-${w.id}`}>
            <div><p className="text-sm font-medium">{w.label}</p><p className="text-xs text-slate-500">{w.hint}</p></div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Layout = ({ children }) => {
  const { user, logout, getAuthHeaders, selectedDashboard, changeDashboard } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  // Applications section - default CLOSED, persist preference in localStorage
  const [applicationsExpanded, setApplicationsExpanded] = useState(() => {
    const saved = localStorage.getItem('nav_applications_expanded');
    return saved === 'true'; // Default false if not set
  });
  
  // Syndications tree section
  const [syndicationsExpanded, setSyndicationsExpanded] = useState(() => {
    const saved = localStorage.getItem('nav_syndications_expanded');
    return saved === 'true';
  });

  // Contracts tree section
  const [contractsExpanded, setContractsExpanded] = useState(() => {
    const saved = localStorage.getItem('nav_contracts_expanded');
    return saved === 'true';
  });

  // Persist applications expanded state
  useEffect(() => {
    localStorage.setItem('nav_applications_expanded', applicationsExpanded.toString());
  }, [applicationsExpanded]);
  
  // Persist syndications expanded state
  useEffect(() => {
    localStorage.setItem('nav_syndications_expanded', syndicationsExpanded.toString());
  }, [syndicationsExpanded]);

  // Persist contracts expanded state
  useEffect(() => {
    localStorage.setItem('nav_contracts_expanded', contractsExpanded.toString());
  }, [contractsExpanded]);
  
  // 11/03: Fee Ledger nav item for syndicators — only when they collect fees (GET fee-ledger/syndicators → collects_fees)
  const [feeLedgerVisible, setFeeLedgerVisible] = useState(false);
  useEffect(() => {
    if (!user || user.roles?.includes('admin') || !user.contact_id) return;
    let alive = true;
    axios.get(`${API}/syndications/fee-ledger/syndicators`, { headers: getAuthHeaders() })
      .then(r => { if (alive) setFeeLedgerVisible(!!(r.data?.[0]?.collects_fees)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user, getAuthHeaders]);

  // Notification badge counts
  const [badgeCounts, setBadgeCounts] = useState({
    pendingApplications: 0,
    pendingCommissions: 0,
    pendingWithdrawals: 0,
    toReview: 0,
    approved: 0,
    declined: 0,
    killed: 0,
    sheetSyncPending: 0,
    pendingDailyImport: 0,
    collectionsNew: 0,
    inboxUnread: 0,
  });

  // Fetch notification counts for admin/underwriter
  useEffect(() => {
    const fetchBadgeCounts = async () => {
      if (!user) return;
      
      const isAdmin = user.roles?.includes('admin');
      const isUnderwriter = user.roles?.includes('underwriter');
      
      if (!isAdmin && !isUnderwriter) return;
      
      try {
        // Fetch pending applications count
        const appsResponse = await axios.get(`${API}/deal-applications`, { 
          headers: getAuthHeaders() 
        });
        const apps = appsResponse.data;
        const pendingApps = apps.filter(app => 
          app.status === 'Pending' || app.status === 'Under Review'
        ).length;
        
        // Count by status for Applications submenu
        const toReview = apps.filter(app => app.status === 'Pending' || app.status === 'Under Review').length;
        const approved = apps.filter(app => app.status === 'Approved').length;
        const declined = apps.filter(app => app.status === 'Rejected' || app.status === 'Declined').length;
        const killed = apps.filter(app => app.status === 'Killed').length;
        
        // Fetch pending commission requests (admin only)
        let pendingCommissions = 0;
        if (isAdmin) {
          try {
            const commResponse = await axios.get(`${API}/commissions/requests?status=pending`, { 
              headers: getAuthHeaders() 
            });
            pendingCommissions = commResponse.data?.length || 0;
          } catch (e) {
            // Commission requests endpoint may not exist
          }
        }
        
        // Fetch pending withdrawals (admin only)
        let pendingWithdrawals = 0;
        let sheetSyncPending = 0;
        let pendingDailyImport = 0;
        let collectionsNew = 0;
        let inboxUnread = 0;
        if (isAdmin) {
          try {
            const withdrawResponse = await axios.get(`${API}/withdrawals?status=pending`, { 
              headers: getAuthHeaders() 
            });
            pendingWithdrawals = withdrawResponse.data?.filter(w => String(w.status || '').toLowerCase() === 'pending').length || 0;   // 10/23: stored as "Pending"
          } catch (e) {
            // Withdrawals endpoint may return differently
          }
          try {
            const syncRes = await axios.get(`${API}/sheets/sync/status`, { headers: getAuthHeaders() });
            sheetSyncPending = syncRes.data?.pending_changes || 0;
          } catch (e) { /* ignore */ }
          try {
            const diRes = await axios.get(`${API}/daily-import/staging?status=pending`, { headers: getAuthHeaders() });
            pendingDailyImport = diRes.data?.count || 0;
          } catch (e) { /* ignore */ }
          try {
            // Collections (10/12): unread merchant replies + open cases with a new bounce
            const colRes = await axios.get(`${API}/collections/badge`, { headers: getAuthHeaders() });
            collectionsNew = colRes.data?.collections ?? colRes.data?.total ?? 0;
            inboxUnread = colRes.data?.unread_messages || 0;
          } catch (e) { /* ignore */ }
        }
        
        setBadgeCounts({
          pendingApplications: pendingApps,
          pendingCommissions,
          pendingWithdrawals,
          toReview,
          approved,
          declined,
          killed,
          sheetSyncPending,
          pendingDailyImport,
          collectionsNew,
          inboxUnread,
        });
      } catch (error) {
        console.error('Failed to fetch badge counts:', error);
      }
    };
    
    fetchBadgeCounts();
    // Refresh every 60 seconds
    const interval = setInterval(fetchBadgeCounts, 60000);
    return () => clearInterval(interval);
  }, [user, getAuthHeaders]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Role-based navigation
  // Admin - Full access to everything
  // Broker - View deals where they're involved, commissions
  // ISO - Submit applications, view their applications, commissions, see their deals
  // Underwriter - Review all applications, change status, convert to deals
  // Syndicator - Syndication dashboard, see their investments
  // Merchant - View only their deals
  // Funder - View deals, reports
  const navItemsBase = [
    { id: 'my-portal', feature: 'investor_portal', path: '/portal', label: 'Investor Portal', icon: User, roles: ['admin', 'syndicator', 'investor'] },
    { id: 'home', path: '/', label: 'Home', icon: LayoutDashboard, roles: ['admin', 'broker', 'syndicator', 'funder', 'iso', 'merchant', 'underwriter'] },
    { id: 'contacts', feature: 'contacts', path: '/contacts', label: 'Address Book', icon: Users, roles: ['admin'] },
    { id: 'contracts', feature: 'documents_generate', path: '/contracts', label: 'Contracts', icon: FileSignature, roles: ['admin'], isContractsTree: true },
    { id: 'deals', feature: 'deals', path: '/deals', label: 'Deals', icon: Briefcase, roles: ['admin', 'broker', 'funder', 'underwriter'] },
    { id: 'uw-toolkit', feature: 'documents_generate', path: '/uw-toolkit', label: 'UW Toolkit', icon: Layers, roles: ['admin'], featureFlag: 'uw_toolkit_enabled' },
    { id: 'uw-admin', feature: 'documents_generate', path: '/uw-admin', label: 'UW Admin', icon: Layers, roles: ['admin'] },
    { id: 'my-deals', feature: 'deals', path: '/deals', label: 'My Deals', icon: Briefcase, roles: ['iso', 'merchant'] },
    { id: 'submit-application', feature: 'applications', path: '/submit-application', label: 'Submit Application', icon: FileText, roles: ['iso', 'broker'] },
    { id: 'my-applications', feature: 'applications', path: '/my-applications', label: 'My Applications', icon: FileText, roles: ['iso', 'broker'] },
    // Applications menu is handled separately below
    { id: 'commissions', feature: 'commissions', path: '/commissions', label: 'Commissions', icon: DollarSign, roles: ['broker', 'iso'] },  // 11/03: admins → Syndications → Money out
    { id: 'reports', feature: 'reports', path: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'funder', 'broker'] },
    { id: 'portfolio', feature: 'reports', path: '/portfolio', label: 'Portfolio', icon: PieChart, roles: ['admin', 'broker', 'funder'] },
    { id: 'syndication-dashboard', path: '/syndication-dashboard', label: 'Syndication Dashboard', icon: HandCoins, roles: ['admin', 'syndicator'], hidden: true },
    { id: 'syndications-tree', feature: 'syndications', path: '/syndications/dashboard', label: 'Syndications', icon: HandCoins, roles: ['admin', 'syndicator'], isTree: true },
    { id: 'withdrawals', feature: 'withdrawals', path: '/syndication-withdrawals', label: 'Withdrawals', icon: CreditCard, roles: ['syndicator'], adminHidden: true },  // 11/03: admins → Syndications → Disbursements
    { id: 'uploads', feature: 'imports', path: '/uploads', label: 'Uploads', icon: Upload, roles: ['admin'] },
    { id: 'bulk-import', feature: 'imports', path: '/bulk-import', label: 'Bulk Import', icon: Upload, roles: ['admin'] },
    { id: 'sheet-import', feature: 'imports', path: '/sheet-import', label: 'Sheet Import', icon: Sheet, roles: ['admin'], badgeKey: 'sheetSyncPending', badgeRoles: ['admin'] },
    { id: 'mca-import', feature: 'imports', path: '/mca-import', label: 'MCA Import', icon: Upload, roles: ['admin'] },
    { id: 'daily-import-review', feature: 'daily_import', path: '/daily-import-review', label: 'Daily Import', icon: ClipboardCheck, roles: ['admin'], badgeKey: 'pendingDailyImport', badgeRoles: ['admin'] },
    { id: 'legal', feature: 'legal', path: '/legal', label: 'Legal', icon: Scale, roles: ['admin'] },
    { id: 'collections', feature: 'collections', path: '/collections', label: 'Collections', icon: MessageSquare, roles: ['admin'], badgeKey: 'collectionsNew', badgeRoles: ['admin'] },
    { id: 'inbox', feature: 'inbox', path: '/inbox', label: 'Inbox', icon: Inbox, roles: ['admin'], badgeKey: 'inboxUnread', badgeRoles: ['admin'] },
    { id: 'data-health', feature: 'system_admin', path: '/data-health', label: 'Data Health', icon: Activity, roles: ['super_admin'] },
    { id: 'admin-jobs', feature: 'system_admin', path: '/admin/jobs', label: 'Jobs', icon: Clock, roles: ['super_admin'] },
    { id: 'mila-memory', feature: 'mila', path: '/mila-memory', label: 'MILA Memory', icon: Brain, roles: ['admin'] },
    { id: 'refi-chains', feature: 'refi_chains', path: '/refi-chains', label: 'Refi Chains', icon: GitBranch, roles: ['admin'] },
    { id: 'settings', path: '/settings', label: 'Settings', icon: Settings, roles: ['admin', 'broker', 'syndicator', 'funder', 'iso', 'merchant', 'underwriter'] },
  ];

  // Get navigation items in custom order (from localStorage or API)
  const getOrderedNavItems = () => {
    try {
      const savedOrder = localStorage.getItem('nav_order');
      if (savedOrder) {
        const orderIds = JSON.parse(savedOrder);
        const orderedItems = [];
        orderIds.forEach(id => {
          const item = navItemsBase.find(i => i.id === id);
          if (item) orderedItems.push(item);
        });
        // Add any items that weren't in the saved order
        navItemsBase.forEach(item => {
          if (!orderedItems.find(i => i.id === item.id)) {
            orderedItems.push(item);
          }
        });
        return orderedItems;
      }
    } catch (e) {
      console.error('Failed to parse nav order:', e);
    }
    return navItemsBase;
  };

  const navItems = getOrderedNavItems();

  // Applications submenu items
  const applicationsSubItems = [
    { path: '/applications?status=to-review', label: 'To Review', icon: Clock, badgeKey: 'toReview' },
    { path: '/applications?status=approved', label: 'Approved', icon: CheckCircle, badgeKey: 'approved' },
    { path: '/applications?status=declined', label: 'Declined', icon: XCircle, badgeKey: 'declined' },
    { path: '/applications?status=killed', label: 'Killed', icon: Skull, badgeKey: 'killed' },
  ];

  const isAdminOrUnderwriter = user?.roles?.includes('admin') || user?.roles?.includes('underwriter');
  
  // Get badge count for a nav item
  const getBadgeCount = (item) => {
    if (!item.badgeKey) return 0;
    // Check if badge should show for current user's role
    if (item.badgeRoles && !user?.roles?.some(r => item.badgeRoles.includes(r))) return 0;
    return badgeCounts[item.badgeKey] || 0;
  };

  const visibleNavItems = navItems.filter((item) => {
    // Skip hidden items
    if (item.hidden) return false;
    // 11/03: items that exist only for non-admin logins (admins have a hub for the same thing)
    if (item.adminHidden && user?.roles?.includes('admin')) return false;
    // 09/22/2026 — retired upload pages (config/retiredPages.js); routes redirect to Daily Import
    if (isRetired(item.id)) return false;
    // 11/02 (Josh): feature-based access — a feature-mapped item shows when the EFFECTIVE feature set grants it
    // (super admin: everything; an admin with no explicit set: its old role rule). Unmapped items keep the role rule.
    if (item.feature && !(isSuperAdmin(user) || isLegacyAdmin(user))) {
      if (!hasFeature(user, item.feature, 'ro')) return false;
      // "Deals" (all deals) vs "My Deals" (own deals) is the data scope, not a feature
      const scope = user?.effective_scope || 'all';
      if (item.id === 'deals' && scope !== 'all') return false;
      if (item.id === 'my-deals' && scope === 'all') return false;
      return true;
    }
    // First check role access
    const hasRoleAccess = user?.roles?.some((userRole) => item.roles.includes(userRole));
    if (!hasRoleAccess) return false;
    
    // Then check feature flag if specified
    if (item.featureFlag) {
      // Admin always has access, OR user has the specific feature flag enabled
      const isAdmin = user?.roles?.includes('admin');
      const hasFeatureFlag = user?.[item.featureFlag] === true;
      return isAdmin || hasFeatureFlag;
    }
    
    return true;
  });

  // Check if current path is an applications path
  const isApplicationsPath = location.pathname.startsWith('/applications') || location.pathname === '/application-review';

  // Syndications submenu items
  const syndicationsSubItems = [
    { path: '/syndications/dashboard', label: 'Dashboard', icon: Landmark },
    { path: '/syndications/unsyndicated', label: 'Needs Syndication', icon: HandCoins },
    { path: '/syndication-dashboard', label: 'Distributions', icon: HandCoins },
    // 11/03 (Josh): Ledger and Fee Ledger are tabs on each syndicator's page for admins; syndicator logins keep the items
    ...(!user?.roles?.includes('admin') ? [{ path: '/syndications/ledger', label: 'Ledger', icon: BookOpen }] : []),
    // 11/03 (Josh): a syndicator sees Fee Ledger only when a position of theirs collects fees; admins always.
    ...((!user?.roles?.includes('admin') && feeLedgerVisible) ? [{ path: '/syndications/fee-ledger', label: 'Fee Ledger', icon: Receipt }] : []),
    ...(user?.roles?.includes('admin') ? [
      // 11/03 (Josh): Payables merged into Distributions; Disbursements = the checkout (weekly payouts + receipts)
      { path: '/syndications/disbursements', label: 'Disbursements', icon: Receipt, badgeKey: 'pendingWithdrawals' },
      // 11/03 (Josh "consolidate"): Payouts + ISO Commissions + Partner Ledger are tabs of one page
      { path: '/syndications/money-out', label: 'Money out', icon: DollarSign, badgeKey: 'pendingCommissions' },
    ] : []),
  ];

  const isSyndicationsPath = location.pathname.startsWith('/syndications') || location.pathname === '/syndication-dashboard';

  // Contracts submenu items
  const contractsSubItems = [
    { path: '/contracts', label: 'All Contracts', icon: FileSignature },
    { path: '/contract-mapping', label: 'Contract Mapping', icon: Link2 },
  ];

  const isContractsPath = location.pathname.startsWith('/contracts') || location.pathname.startsWith('/contract-mapping');

  const renderNavLinks = (onNavigate) => (
    <nav className="mt-4 space-y-1">
      {visibleNavItems.map((item, index) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        const baseClasses =
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors pfs-nav-link';
        const activeClasses = 'bg-blue-50 text-blue-600 pfs-nav-active';
        const inactiveClasses =
          'text-slate-600 hover:text-blue-600 hover:bg-slate-50 pfs-nav-inactive';
        const badgeCount = getBadgeCount(item);

        // Insert Applications menu after "My Applications" or before "Commissions"
        const showApplicationsMenu = item.label === 'Commissions' && isAdminOrUnderwriter;

        return (
          <React.Fragment key={`${item.path}-${item.label}-${index}`}>
            {showApplicationsMenu && (
              <>
                {/* Applications Parent Menu */}
                <div className="space-y-1">
                  <button
                    onClick={() => setApplicationsExpanded(!applicationsExpanded)}
                    className={`w-full ${baseClasses} ${isApplicationsPath ? activeClasses : inactiveClasses}`}
                  >
                    <ClipboardCheck className="h-4 w-4" />
                    <span className="flex-1 text-left">Applications</span>
                    {badgeCounts.toReview > 0 && (
                      <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                        {badgeCounts.toReview}
                      </span>
                    )}
                    {applicationsExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </button>
                  
                  {/* Applications Sub Items */}
                  {applicationsExpanded && (
                    <div className="ml-4 pl-3 border-l border-slate-200 space-y-1">
                      {applicationsSubItems.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = location.pathname + location.search === subItem.path ||
                          (location.pathname === '/applications' && location.search.includes(subItem.path.split('?')[1]));
                        const subBadgeCount = badgeCounts[subItem.badgeKey] || 0;
                        
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => onNavigate && onNavigate()}
                            className={`${baseClasses} ${isSubActive ? activeClasses : inactiveClasses}`}
                          >
                            <SubIcon className="h-4 w-4" />
                            <span className="flex-1">{subItem.label}</span>
                            {subBadgeCount > 0 && (
                              <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
                                subItem.label === 'To Review' ? 'text-white bg-orange-500' :
                                subItem.label === 'Approved' ? 'text-white bg-green-500' :
                                subItem.label === 'Declined' ? 'text-white bg-red-500' :
                                'text-white bg-gray-500'
                              }`}>
                                {subBadgeCount}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
            {/* Contracts Tree */}
            {item.isContractsTree ? (
              <div className="space-y-1">
                <button
                  onClick={() => setContractsExpanded(!contractsExpanded)}
                  className={`w-full ${baseClasses} ${isContractsPath ? activeClasses : inactiveClasses}`}
                  data-testid="nav-contracts-tree"
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {contractsExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                {contractsExpanded && (
                  <div className="ml-4 pl-3 border-l border-slate-200 space-y-1">
                    {contractsSubItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname === subItem.path;
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => onNavigate && onNavigate()}
                          className={`${baseClasses} ${isSubActive ? activeClasses : inactiveClasses}`}
                          data-testid={`nav-contracts-${subItem.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <SubIcon className="h-4 w-4" />
                          <span className="flex-1">{subItem.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : /* Syndications Tree */
            item.isTree ? (
              <div className="space-y-1">
                <button
                  onClick={() => setSyndicationsExpanded(!syndicationsExpanded)}
                  className={`w-full ${baseClasses} ${isSyndicationsPath ? activeClasses : inactiveClasses}`}
                  data-testid="nav-syndications-tree"
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {syndicationsExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
                {syndicationsExpanded && (
                  <div className="ml-4 pl-3 border-l border-slate-200 space-y-1">
                    {syndicationsSubItems.map((subItem) => {
                      const SubIcon = subItem.icon;
                      const isSubActive = location.pathname === subItem.path;
                      return (
                        <Link
                          key={subItem.path}
                          to={subItem.path}
                          onClick={() => onNavigate && onNavigate()}
                          className={`${baseClasses} ${isSubActive ? activeClasses : inactiveClasses}`}
                          data-testid={`nav-syndications-${subItem.label.toLowerCase()}`}
                        >
                          <SubIcon className="h-4 w-4" />
                          <span className="flex-1">{subItem.label}</span>
                          {subItem.badgeKey && (badgeCounts[subItem.badgeKey] || 0) > 0 && (
                            <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{badgeCounts[subItem.badgeKey]}</span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
            <Link
              to={item.path}
              onClick={() => {
                if (onNavigate) onNavigate();
              }}
              className={`${baseClasses} ${isActive ? activeClasses : inactiveClasses}`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {badgeCount > 0 && (
                <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
            </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex pfs-shell">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 bg-white border-r pfs-sidebar">
        <div className="flex flex-col w-full h-screen sticky top-0">
          <div className="h-16 flex items-center px-4 border-b">
            <WorkspaceSwitcher pathname={location.pathname} navigate={navigate} />
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-4">
            <p className="text-xs font-semibold text-slate-500 px-2 mb-2 uppercase tracking-wide">
              Navigation
            </p>
            {renderNavLinks()}
          </div>

          <div className="border-t px-4 py-3 text-xs text-slate-500">
            <p className="font-medium text-slate-700">{user?.name}</p>
            <p className="capitalize">{user?.roles?.[0]}</p>
          </div>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 bg-white border-b flex items-center justify-between px-4 sm:px-6 pfs-topbar">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open navigation</span>
              <LayoutDashboard className="h-5 w-5" />
            </button>
            <span className="text-base font-semibold text-slate-900 hidden md:inline-block">
              Dashboard
            </span>
            
            {/* Dashboard Selector - always visible for non-admin users with multiple roles */}
            <div className="hidden md:block ml-4">
              <DashboardSelector 
                selectedDashboard={selectedDashboard}
                onDashboardChange={changeDashboard}
                userRoles={user?.roles}
              />
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Mobile Dashboard Selector */}
            <div className="md:hidden">
              <DashboardSelector 
                selectedDashboard={selectedDashboard}
                onDashboardChange={changeDashboard}
                userRoles={user?.roles}
              />
            </div>
            <div className="hidden sm:block">
              <GlobalSearch />
            </div>
            <NotificationBell />
            <div className="hidden sm:block text-sm text-right">
              <p className="font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.roles?.[0]}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </header>

        {/* Mobile slide-out sidebar */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 flex md:hidden">
            <div
              className="fixed inset-0 bg-black/30"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="relative flex w-64 max-w-full flex-1 flex-col bg-white border-r pfs-sidebar">
              <div className="h-16 flex items-center justify-between px-4 border-b">
                <WorkspaceSwitcher pathname={location.pathname} navigate={navigate} />
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close navigation</span>
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-4">
                {renderNavLinks(() => setSidebarOpen(false))}
              </div>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          {children}
        </main>
      </div>

      {/* Floating assistant — position:fixed, does not affect page layout */}
      <AgentWidget />
    </div>
  );
};

export default Layout;
